/**
 * One-time SSO callback code.
 *
 * The callback page mounts twice on a cold load: `login` is not a core
 * namespace, so `useTranslation('login')` changes `t` when the bundle arrives,
 * and the effect that listed `t` as a dependency posted the code again.
 * React StrictMode does the same in dev. The API consumes the code on the
 * first POST, so the second response is "Invalid or expired code" and the
 * page used to discard the login that had already succeeded.
 */

const inflight = new Map();

/**
 * @param {{ code?: string|null, hasSession?: boolean }} input
 * @returns {'home'|'login'|'exchange'}
 */
export function decideSsoCallback({ code, hasSession }) {
    if (hasSession) return 'home';
    if (!code) return 'login';
    return 'exchange';
}

/**
 * Share one exchange POST for a code across effect restarts in this document.
 * A later caller (StrictMode remount, back/forward inside the SPA) gets the
 * same result instead of burning the code.
 *
 * @template T
 * @param {string} code
 * @param {(code: string) => Promise<T>} exchange
 * @returns {Promise<T>}
 */
export function exchangeSsoCodeOnce(code, exchange) {
    const key = String(code);
    const existing = inflight.get(key);
    if (existing) return existing;

    const pending = Promise.resolve().then(() => exchange(key));
    inflight.set(key, pending);
    return pending;
}

/** @internal test isolation */
export function resetSsoCodeExchangeForTests() {
    inflight.clear();
}
